import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import {
  LogOut, Home, LayoutDashboard, UserCircle, CalendarCheck, Clock,
  CreditCard, Receipt, Landmark, Star, BookOpen, Megaphone, FileText,
  Bell, Search, Users, Crosshair, Settings, HandCoins, Menu, X,
} from "lucide-react";
import { logout } from "../../redux/actions/authActions";
import { ToastProvider } from "./ToastContext";
import ForcePasswordChange from "../auth/ForcePasswordChange";
import { logoWhite } from "../../assets";
import { get, put } from "../../services/lewooApi";
import AdminLanguage from "../language/AdminLanguage";
import EmployeeAvatar from "./EmployeeAvatar";

const CORE_SECTIONS = [
  {
    labelId: "ess.section.overview",
    items: [
      { to: "/admin/dashboard", icon: LayoutDashboard, label: "ess.nav.dashboard", end: true, keywords: "home accueil tableau de bord" },
      { to: "/admin/profile", icon: UserCircle, label: "ess.nav.profile", keywords: "profil compte informations personnel" },
    ],
  },
  {
    labelId: "ess.section.timeLeave",
    items: [
      { to: "/admin/leaves", icon: CalendarCheck, label: "ess.nav.leaves", keywords: "congé leave permission absence" },
      { to: "/admin/attendance", icon: Clock, label: "ess.nav.attendance", keywords: "présence pointage heure arrivée" },
      { to: "/admin/timesheets", icon: Clock, label: "ess.nav.timesheets", keywords: "temps feuille timesheet projet heures" },
    ],
  },
  {
    labelId: "ess.section.payFinance",
    items: [
      { to: "/admin/payslips", icon: CreditCard, label: "ess.nav.payslips", keywords: "salaire paie bulletin fiche" },
      { to: "/admin/expenses", icon: Receipt, label: "ess.nav.expenses", keywords: "dépense note frais remboursement" },
      { to: "/admin/loans", icon: Landmark, label: "ess.nav.loans", keywords: "prêt emprunt avance salaire" },
      { to: "/admin/salary-advances", icon: HandCoins, label: "ess.nav.salaryAdvances", keywords: "avance salaire advance" },
    ],
  },
  {
    labelId: "ess.section.performance",
    items: [
      { to: "/admin/appraisals", icon: Star, label: "ess.nav.appraisals", keywords: "évaluation performance appraisal review" },
      { to: "/admin/training", icon: BookOpen, label: "ess.nav.training", keywords: "formation training cours certification" },
      { to: "/admin/documents", icon: FileText, label: "ess.nav.documents", keywords: "document fichier contrat certificat" },
      { to: "/admin/announcements", icon: Megaphone, label: "ess.nav.announcements", keywords: "annonce communication information" },
    ],
  },
];

const MANAGEMENT_ITEMS = [
  { to: "/admin/employees", icon: Users, label: "ess.nav.employees", permission: "canViewEmployees", keywords: "employés employees directory annuaire personnel" },
  { to: "/admin/recruitment", icon: Crosshair, label: "ess.nav.recruitment", permission: "canViewRecruitment", keywords: "recrutement recruitment job hiring embauche" },
  { to: "/admin/settings", icon: Settings, label: "ess.nav.settings", permission: "isSettingsAdmin", keywords: "paramètres settings configuration config" },
];

const AdminLayout = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions } = useSelector((state) => state.auth);
  const notifRef = useRef(null);

  const SECTIONS = useMemo(() => {
    const sections = [...CORE_SECTIONS];

    const mgmtItems = permissions?.isSpecial ? MANAGEMENT_ITEMS : [];
    if (mgmtItems.length > 0) {
      sections.push({
        labelId: "ess.section.administration",
        items: mgmtItems,
      });
    }

    return sections;
  }, [permissions]);

  const MODULES = useMemo(() =>
    SECTIONS.flatMap((s) =>
      s.items.map((i) => ({ ...i, sectionId: s.labelId, keywords: (i.keywords || "") + " " + i.label }))
    ),
    [SECTIONS]
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentEmp, setCurrentEmp] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await get("/api/employees?limit=500");
        const rows = res?.data || [];
        const found = rows.find((e) =>
          (user?.employee_id != null && (e.id === user.employee_id || e.employee_id === user.employee_id)) ||
          (user?.email && (e.work_email === user.email || e.email === user.email))
        ) || null;
        if (!cancelled) setCurrentEmp(found);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [user]);

  const loadNotifications = useCallback(async () => {
    try {
      const result = await get("/api/announcements?limit=50");
      const rows = result?.data || [];
      const unread = rows
        .filter((a) => !a.read)
        .map((a) => ({
          id: a.lewoo_uid ?? a.id,
          title: a.name || a.title,
          preview: a.body || a.description || "",
          date: a.date_start ? new Date(a.date_start).toLocaleDateString(intl.locale) : "",
        }));
      setNotifications(unread);
    } catch {}
  }, [intl]);

  useEffect(() => {
    let cancelled = false;
    loadNotifications().then(() => { cancelled = true; });
    return () => { cancelled = true; };
  }, [loadNotifications, location.pathname]);

  useEffect(() => {
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (permissions?.isAdmin) {
      const target = location.pathname.replace("/admin", "/admin-panel");
      navigate(target, { replace: true });
    }
  }, [permissions, navigate, location.pathname]);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return MODULES.filter((m) => {
      const label = intl.formatMessage({ id: m.label }).toLowerCase();
      const section = intl.formatMessage({ id: m.sectionId }).toLowerCase();
      return label.includes(q) || section.includes(q) || m.keywords.toLowerCase().includes(q);
    });
  }, [searchQuery, intl]);

  const handleSearchSelect = (to) => {
    setSearchQuery("");
    setSearchOpen(false);
    navigate(to);
  };

  const handleNotifClick = async (ann) => {
    if (ann.id != null) {
      await put(`/api/announcements/${ann.id}/read`).catch(() => {});
    }
    setNotifications((prev) => prev.filter((n) => n.id !== ann.id));
    setNotifOpen(false);
    navigate("/admin/announcements");
  };

  const handleLogout = async () => {
    await dispatch(logout());
    navigate("/admin/login");
  };

  const pageTitle = () => {
    for (const s of SECTIONS)
      for (const i of s.items)
        if (location.pathname === i.to || (i.end === false && location.pathname.startsWith(i.to)))
          return intl.formatMessage({ id: i.label });
    return intl.formatMessage({ id: "ess.layout.pageTitleFallback" });
  };

  const unreadCount = notifications.length;

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!searchOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setSearchOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [searchOpen]);

  return (
    <div className="min-h-screen flex bg-slate-50 font-sans antialiased">
      <ForcePasswordChange
        visible={!!user?.must_change_password}
        onClose={() => {}}
        onSuccess={() => {}}
      />
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}
      <aside className={`fixed top-0 left-0 w-64 ess-drawer flex flex-col z-40 overflow-y-auto transition-transform duration-200 lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ background: "#14532d" }}
      >
        <div className="flex items-center gap-3 px-4 py-[18px] border-b border-white/10 flex-shrink-0">
          <img src={logoWhite} alt="Africa Systems" className="w-8 h-8 object-contain flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <h1 className="text-sm font-extrabold text-white leading-tight">
              {intl.formatMessage({ id: "ess.layout.portalTitle" })}
            </h1>
            <p className="text-[10px] text-white/40 mt-px">
              {intl.formatMessage({ id: "ess.layout.portalSubtitle" })}
            </p>
          </div>
          <button onClick={() => setMobileOpen(false)} className="lg:hidden text-white/60 hover:text-white transition-colors" aria-label={intl.formatMessage({ id: "ess.common.close" })}>
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 px-2 py-2">
          {SECTIONS.map((section) => (
            <div key={section.labelId}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/30 px-2.5 py-1 mt-2">
                {intl.formatMessage({ id: section.labelId })}
              </p>
              {section.items.map(({ to, icon: Icon, label, end }) => {
                const active = end ? location.pathname === to : location.pathname.startsWith(to);
                return (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    className={`flex items-center gap-2.5 px-2.5 py-[8px] rounded-md text-sm font-medium transition-colors mb-[2px] ${
                      active
                        ? "bg-emerald-500/20 text-emerald-200 font-semibold"
                        : "text-white/55 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    <Icon size={16} className="flex-shrink-0" />
                    <span className="flex-1 truncate">{intl.formatMessage({ id: label })}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="px-2.5 py-2.5 border-t border-white/10 flex-shrink-0">
          <div className="flex items-center gap-2.5 px-2 py-2">
            <EmployeeAvatar employee={currentEmp || { name: user?.username || user?.displayName || user?.email || "U" }} size={32} fontSize={13} />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-white truncate">
                {user?.username || intl.formatMessage({ id: "ess.profile.employeeType" })}
              </p>
              <p className="text-[11px] text-white/40 truncate">{user?.email || ""}</p>
            </div>
          </div>
        </div>
      </aside>

      <div className="lg:ml-64 flex-1 min-h-screen">
        <ToastProvider>
        <header className="fixed top-0 left-0 lg:left-64 right-0 h-14 bg-white border-b border-slate-200 flex items-center px-4 sm:px-6 gap-3 z-30 shadow-sm">
          <button onClick={() => setMobileOpen(true)} className="lg:hidden h-9 w-9 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 transition-colors" aria-label={intl.formatMessage({ id: "admin.nav.menu" })}>
            <Menu size={20} />
          </button>
          <span className="hidden sm:inline text-sm font-bold text-slate-800 truncate min-w-0">{pageTitle()}</span>

          <div className="ml-auto flex items-center gap-2">
            <AdminLanguage />
            <button
              onClick={() => { setSearchOpen(true); setSearchQuery(""); }}
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
              title={intl.formatMessage({ id: "admin.nav.search" })}
              aria-label={intl.formatMessage({ id: "admin.nav.search" })}
            >
              <Search size={16} />
              <span className="hidden sm:inline text-xs font-medium">
                {intl.formatMessage({ id: "admin.nav.search" })}
              </span>
            </button>

            <div ref={notifRef} className="relative">
              <button
                onClick={() => {
                  if (!notifOpen) loadNotifications();
                  setNotifOpen(!notifOpen);
                }}
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 relative transition-colors"
                aria-label={intl.formatMessage({ id: "admin.nav.notifications" })}
                title={intl.formatMessage({ id: "admin.nav.notifications" })}
              >
                <Bell size={16} />
                <span className="hidden sm:inline text-xs font-medium">
                  {intl.formatMessage({ id: "admin.nav.notifications" })}
                </span>
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border-2 border-white">
                    {unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-gray-200 shadow-xl z-50 animate-scale-in max-h-96 overflow-y-auto">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-bold text-gray-900">
                      {intl.formatMessage({ id: "ess.notifications.title" })}
                    </h3>
                  </div>
                  {notifications.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-400">
                      <span className="text-2xl block mb-2">🔔</span>
                      {intl.formatMessage({ id: "ess.notifications.empty" })}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {notifications.map((ann) => (
                        <button
                          key={ann.id}
                          onClick={() => handleNotifClick(ann)}
                          className="w-full text-left px-4 py-3 hover:bg-blue-50/50 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">{ann.title}</p>
                              <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{ann.preview}</p>
                              <p className="text-[11px] text-gray-400 mt-1">{ann.date}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  <Link
                    to="/admin/announcements"
                    onClick={() => setNotifOpen(false)}
                    className="block px-4 py-2.5 text-center text-sm font-medium text-emerald-600 hover:bg-emerald-50 border-t border-gray-100"
                  >
                    {intl.formatMessage({ id: "ess.notifications.viewAll" })}
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/"
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-sm text-slate-500 hover:bg-slate-100 hover:text-emerald-600 transition-colors"
              title={intl.formatMessage({ id: "admin.nav.homeSite" })}
            >
              <Home size={16} />
              <span className="hidden sm:inline text-xs font-medium">
                {intl.formatMessage({ id: "admin.nav.homeSite" })}
              </span>
            </Link>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-sm text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors"
              title={intl.formatMessage({ id: "admin.nav.logout" })}
              aria-label={intl.formatMessage({ id: "admin.nav.logout" })}
            >
              <LogOut size={16} />
              <span className="hidden sm:inline text-xs font-medium">
                {intl.formatMessage({ id: "admin.nav.logout" })}
              </span>
            </button>
          </div>
        </header>

        <main className="pt-[72px] p-4 sm:p-6">
          <Outlet />
        </main>
        </ToastProvider>
      </div>

      {searchOpen && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[10vh]">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setSearchOpen(false)} />
          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-scale-in">
            <div className="flex items-center gap-3 px-4">
              <Search size={16} className="text-slate-400 flex-shrink-0" />
              <input
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={intl.formatMessage({ id: "ess.common.search" })}
                className="w-full py-3.5 text-sm bg-transparent outline-none placeholder:text-slate-400"
              />
              <button
                onClick={() => setSearchOpen(false)}
                className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                aria-label={intl.formatMessage({ id: "ess.common.close" })}
              >
                <X size={16} />
              </button>
            </div>
            <div className="border-t border-slate-100 max-h-80 overflow-y-auto py-1">
              {searchQuery.trim() && searchResults.length > 0 && searchResults.map((r) => (
                <button
                  key={r.to}
                  onClick={() => handleSearchSelect(r.to)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-emerald-50 transition-colors text-left"
                >
                  <r.icon size={15} className="text-slate-400 flex-shrink-0" />
                  <span className="flex-1 font-medium text-gray-700">{intl.formatMessage({ id: r.label })}</span>
                  <span className="text-[10px] text-gray-400 uppercase flex-shrink-0">
                    {intl.formatMessage({ id: r.sectionId })}
                  </span>
                </button>
              ))}
              {searchQuery.trim() && searchResults.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-gray-400">
                  {intl.formatMessage({ id: "ess.common.noModulesFound" })}
                </div>
              )}
              {!searchQuery.trim() && (
                <div className="px-4 py-6 text-center text-sm text-gray-400">
                  {intl.formatMessage({ id: "ess.common.searchHint" })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLayout;
